import { siteApiSlice } from "../../app/siteApiSlice";

export enum SecretAuditAction {
	SecretChanged = "SecretChanged",
	SecretRestored = "SecretRestored",
	/** Key Vault refused a change, so the secret wasn't changed. */
	SecretChangeFailed = "SecretChangeFailed",
	/** Key Vault refused a restore, or there was no previous value, so the secret wasn't changed. */
	SecretRestoreFailed = "SecretRestoreFailed",
	AccessGranted = "AccessGranted",
	AccessRevoked = "AccessRevoked",
	PasswordCheckFailed = "PasswordCheckFailed",
}

export interface SecretViewModel {
	name: string;
	description: string;
	isSet: boolean;
	updatedOn?: string | null;
	/** The username in the current version's updatedBy tag. */
	updatedBy?: string | null;
	updatedByFullName?: string | null;
	hasPreviousVersion: boolean;
	needsRestart: boolean;
	/** Every version from Key Vault's history, including disabled ones, newest first. */
	versions: SecretVersionViewModel[];
}

export interface SecretVersionViewModel {
	version: string;
	createdOn?: string | null;
	enabled: boolean;
	/** The username of who saved it on the Secrets page; empty when it was set some other way. */
	updatedBy?: string | null;
	updatedByFullName?: string | null;
	changedOnSecretsPage: boolean;
	/** For a restore, the version whose value was copied. */
	restoredFrom?: string | null;
}

export interface SecretManagerViewModel {
	id: number;
	userName: string;
	fullName: string;
	email: string;
	accessGrantedAtUtc?: string;
	isYou: boolean;
}

export interface SecretChangeViewModel {
	id: number;
	changedAtUtc: string;
	changedBy: string;
	changedByFullName?: string | null;
	action: SecretAuditAction;
	secretName?: string | null;
	targetUser?: string | null;
	targetUserFullName?: string | null;
	ipAddress?: string | null;
}

export interface SecretsStatusViewModel {
	canManageSecrets: boolean;
	canManageAccess: boolean;
	nobodyCanManageSecrets: boolean;
	keyVaultAvailable: boolean;
	keyVaultError?: string;
	cacheMinutes: number;
	secrets?: SecretViewModel[];
	managers?: SecretManagerViewModel[];
	availableSuperAdministrators?: SecretManagerViewModel[];
	/** Changes to secrets, for the Secrets tab. Only sent to people who can manage secrets. */
	recentSecretChanges?: SecretChangeViewModel[];
	/** Changes to who can manage secrets, for the Managers tab. Only sent to people who can manage secrets. */
	recentAccessChanges?: SecretChangeViewModel[];
}

export interface ChangeSecretArgs {
	secret: string;
	newValue: string;
	password: string;
}

export interface RestoreSecretArgs {
	secret: string;
	password: string;
}

export interface SetSecretAccessArgs {
	userId: number;
	canManageSecrets: boolean;
	password: string;
}

export const secretsApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["Secrets"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getSecretsStatus: build.query<SecretsStatusViewModel, void>({
				query: () => ({
					url: `/Secrets/Status`,
					method: "GET",
				}),
				providesTags: ["Secrets"],
			}),
			changeSecret: build.mutation<void, ChangeSecretArgs>({
				query: (body) => ({
					url: `/Secrets/ChangeSecret`,
					method: "POST",
					body,
				}),
				invalidatesTags: ["Secrets"],
			}),
			restoreSecret: build.mutation<void, RestoreSecretArgs>({
				query: (body) => ({
					url: `/Secrets/RestoreSecret`,
					method: "POST",
					body,
				}),
				invalidatesTags: ["Secrets"],
			}),
			setSecretAccess: build.mutation<void, SetSecretAccessArgs>({
				query: (body) => ({
					url: `/Secrets/SetAccess`,
					method: "POST",
					body,
				}),
				invalidatesTags: ["Secrets"],
			}),
		}),
	});

export const {
	useGetSecretsStatusQuery,
	useChangeSecretMutation,
	useRestoreSecretMutation,
	useSetSecretAccessMutation,
} = secretsApiSlice;
